import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Key,
  AlertTriangle,
  RefreshCw,
  Copy,
  Check,
  Loader2,
  Smartphone,
  QrCode,
  Download,
  ChevronRight,
  Lock,
  Clock,
  CheckCircle2,
  XCircle,
  Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import adminOpsService from '../../services/adminOpsService';

const TwoFactorSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  
  // Dialogs
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  
  // Form state
  const [code, setCode] = useState('');
  const [disableReason, setDisableReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  
  // Enrollment steps
  const [setupStep, setSetupStep] = useState(1); // 1: QR, 2: Verify, 3: Backup Codes
  const [newBackupCodes, setNewBackupCodes] = useState([]);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('fundelite_token')}`,
    'Content-Type': 'application/json',
  }), []);

  const fetchStatus = useCallback(async () => {
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const data = await adminOpsService.get2FAStatus();
      setStatus(data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
      toast.error('Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSetup = async () => {
    setProcessing(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const data = await adminOpsService.setup2FA('totp');
      
      if (data.success) {
        setSetupData(data);
        setSetupStep(1);
        setShowSetupDialog(true);
      } else {
        toast.error(data.error || 'Failed to initiate 2FA setup');
      }
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      toast.error(error.message || 'Failed to initiate 2FA setup');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    if (code.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setProcessing(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const data = await adminOpsService.enable2FA(code);
      
      if (data.success) {
        toast.success('2FA enabled successfully!');
        setSetupStep(3); // Move to backup codes step
        fetchStatus();
      } else {
        toast.error(data.error || data.hint || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      toast.error(error.message || 'Failed to enable 2FA');
    } finally {
      setProcessing(false);
    }
  };

  const handleDisable = async () => {
    if (code.length < 6) {
      toast.error('Please enter a valid code');
      return;
    }
    
    setProcessing(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const data = await adminOpsService.disable2FA(code, disableReason);
      
      if (data.success) {
        toast.success('2FA disabled');
        setShowDisableDialog(false);
        setCode('');
        setDisableReason('');
        fetchStatus();
      } else {
        toast.error(data.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error(error.message || 'Failed to disable 2FA');
    } finally {
      setProcessing(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (code.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setProcessing(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const data = await adminOpsService.regenerateBackupCodes(code);
      
      if (data.success || data.backup_codes) {
        setNewBackupCodes(data.backup_codes);
        setShowRegenerateDialog(false);
        setShowBackupCodesDialog(true);
        setCode('');
        toast.success('Backup codes regenerated');
        fetchStatus();
      } else {
        toast.error(data.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Error regenerating backup codes:', error);
      toast.error(error.message || 'Failed to regenerate backup codes');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(index);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success('Code copied');
  };

  const copySecretKey = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
      toast.success('Secret key copied');
    }
  };

  const downloadBackupCodes = (codes) => {
    const content = `FundElite 2FA Backup Codes
Generated: ${new Date().toISOString()}

Each code can only be used once.
Store these codes in a safe place.

${codes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

If you lose access to your authenticator app,
use one of these codes to sign in.
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fundelite-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded');
  };

  const resetSetupDialog = () => {
    setShowSetupDialog(false);
    setSetupData(null);
    setSetupStep(1);
    setCode('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="2fa-loading">
        <Loader2 className="animate-spin text-amber-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="2fa-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Shield className="text-amber-400" />
          Two-Factor Authentication
        </h1>
        <p className="text-slate-400 mt-1">
          Add an extra layer of security to protect critical admin actions
        </p>
      </div>

      {/* Status Card */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              status?.enabled ? 'bg-emerald-500/20' : 'bg-stone-800'
            }`}>
              {status?.enabled ? (
                <ShieldCheck className="text-emerald-400" size={28} />
              ) : (
                <ShieldOff className="text-slate-500" size={28} />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {status?.enabled ? '2FA is Enabled' : '2FA is Disabled'}
              </h2>
              <p className="text-sm text-slate-400">
                {status?.enabled 
                  ? 'Your account has enhanced security protection'
                  : 'Enable 2FA to protect critical admin actions'}
              </p>
              {status?.enabled_at_utc && (
                <p className="text-xs text-slate-500 mt-1">
                  Enabled: {new Date(status.enabled_at_utc).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          
          {status?.enabled ? (
            <Button 
              variant="outline" 
              className="text-red-400 border-red-500/30 hover:bg-red-500/10"
              onClick={() => setShowDisableDialog(true)}
              data-testid="disable-2fa-btn"
            >
              <ShieldOff size={16} className="mr-2" />
              Disable 2FA
            </Button>
          ) : (
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleSetup}
              disabled={processing}
              data-testid="enable-2fa-btn"
            >
              {processing ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Shield size={16} className="mr-2" />
              )}
              Enable 2FA
            </Button>
          )}
        </div>

        {/* Stats when enabled */}
        {status?.enabled && (
          <div className="mt-6 pt-6 border-t border-stone-800 grid grid-cols-3 gap-4">
            <div className="bg-stone-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                <Key size={14} />
                Backup Codes
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xl font-semibold text-white">
                  {status.backup_codes_remaining ?? '—'}
                </span>
                {status.backup_codes_remaining < 3 && (
                  <AlertTriangle size={16} className="text-amber-400" />
                )}
              </div>
              <Button 
                size="sm" 
                variant="ghost" 
                className="mt-2 w-full text-xs"
                onClick={() => setShowRegenerateDialog(true)}
                data-testid="regenerate-codes-btn"
              >
                <RefreshCw size={12} className="mr-1" />
                Regenerate
              </Button>
            </div>
            
            <div className="bg-stone-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                <CheckCircle2 size={14} />
                Verifications
              </div>
              <span className="text-xl font-semibold text-white">
                {status.verification_count ?? 0}
              </span>
            </div>
            
            <div className="bg-stone-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                <Clock size={14} />
                Last Verified
              </div>
              <span className="text-sm text-white">
                {status.last_verified_at_utc 
                  ? new Date(status.last_verified_at_utc).toLocaleDateString()
                  : 'Never'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Supported Apps */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Smartphone size={18} className="text-amber-400" />
          Supported Authenticator Apps
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['Google Authenticator', 'Microsoft Authenticator', 'Authy', '1Password', 'Any TOTP App'].map((app) => (
            <div key={app} className="bg-stone-800/50 rounded-lg p-3 text-center">
              <p className="text-sm text-slate-300">{app}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Protected Actions */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Lock size={18} className="text-amber-400" />
          Protected Actions
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          These critical actions require 2FA verification when enabled:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(status?.protected_actions || []).map((action) => (
            <div key={action.action} className="flex items-center gap-3 bg-stone-800/30 rounded-lg p-3">
              <div className="w-8 h-8 rounded bg-amber-500/20 flex items-center justify-center">
                <Lock size={14} className="text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{action.label}</p>
                <p className="text-xs text-slate-500">{action.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Future: Phone Authentication */}
      <div className="bg-stone-900/50 border border-stone-800/50 rounded-xl p-6 opacity-60">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-stone-800 flex items-center justify-center">
            <Smartphone size={20} className="text-slate-500" />
          </div>
          <div>
            <h3 className="font-medium text-slate-400">Phone Number Authentication</h3>
            <p className="text-xs text-slate-500">SMS-based authentication coming soon</p>
          </div>
          <span className="ml-auto text-xs bg-stone-800 text-slate-400 px-2 py-1 rounded">
            Coming Soon
          </span>
        </div>
      </div>

      {/* ============================================
          SETUP DIALOG (Multi-step Enrollment)
          ============================================ */}
      <Dialog open={showSetupDialog} onOpenChange={resetSetupDialog}>
        <DialogContent className="max-w-lg bg-stone-900 border-slate-700" data-testid="2fa-setup-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="text-amber-400" />
              {setupStep === 1 && 'Step 1: Scan QR Code'}
              {setupStep === 2 && 'Step 2: Verify Setup'}
              {setupStep === 3 && 'Step 3: Save Backup Codes'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {setupStep === 1 && 'Scan this QR code with your authenticator app'}
              {setupStep === 2 && 'Enter the 6-digit code from your authenticator app'}
              {setupStep === 3 && 'Save these backup codes in a safe place'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3].map((step) => (
              <div 
                key={step}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  setupStep === step 
                    ? 'bg-amber-600 text-white' 
                    : setupStep > step 
                      ? 'bg-emerald-600 text-white'
                      : 'bg-stone-800 text-slate-500'
                }`}
              >
                {setupStep > step ? <Check size={14} /> : step}
              </div>
            ))}
          </div>
          
          {setupData && (
            <div className="space-y-4 py-4">
              {/* STEP 1: QR Code */}
              {setupStep === 1 && (
                <>
                  <div className="flex justify-center">
                    <div className="bg-white p-3 rounded-lg">
                      <img 
                        src={setupData.qr_code} 
                        alt="2FA QR Code" 
                        className="w-48 h-48"
                        data-testid="2fa-qr-code"
                      />
                    </div>
                  </div>
                  
                  {/* Manual entry key */}
                  <div className="bg-stone-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-500">Can't scan? Enter this key manually:</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={copySecretKey}
                        className="h-6 px-2"
                      >
                        {copiedSecret ? <Check size={12} /> : <Copy size={12} />}
                      </Button>
                    </div>
                    <code className="text-sm text-amber-400 bg-stone-900 px-3 py-2 rounded block text-center font-mono tracking-widest break-all" data-testid="2fa-secret-key">
                      {setupData.secret}
                    </code>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      onClick={() => setSetupStep(2)}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Continue
                      <ChevronRight size={16} className="ml-1" />
                    </Button>
                  </div>
                </>
              )}
              
              {/* STEP 2: Verify */}
              {setupStep === 2 && (
                <>
                  <div className="space-y-4">
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info size={18} className="text-emerald-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-emerald-300">Open your authenticator app</p>
                          <p className="text-xs text-emerald-400/70 mt-1">
                            Enter the 6-digit code displayed for FundElite Admin
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-300">Verification Code</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="000000"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="bg-stone-800 border-slate-700 text-center text-2xl tracking-[0.5em] font-mono h-14"
                        maxLength={6}
                        autoFocus
                        data-testid="2fa-verify-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-between">
                    <Button 
                      variant="outline" 
                      onClick={() => setSetupStep(1)}
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={handleVerifyAndEnable}
                      disabled={code.length !== 6 || processing}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="2fa-verify-btn"
                    >
                      {processing ? (
                        <Loader2 size={14} className="mr-2 animate-spin" />
                      ) : (
                        <ShieldCheck size={14} className="mr-2" />
                      )}
                      Verify & Enable
                    </Button>
                  </div>
                </>
              )}
              
              {/* STEP 3: Backup Codes */}
              {setupStep === 3 && (
                <>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle2 size={18} />
                      <p className="font-medium">2FA Successfully Enabled!</p>
                    </div>
                  </div>
                  
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-300 font-medium">Save your backup codes!</p>
                        <p className="text-xs text-amber-400/70 mt-1">
                          If you lose access to your authenticator app, you can use these codes to sign in. Each code can only be used once.
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      {(setupData.backup_codes || []).map((backupCode, i) => (
                        <div key={i} className="flex items-center justify-between bg-stone-900 px-3 py-2 rounded">
                          <code className="text-sm text-slate-300 font-mono">{backupCode}</code>
                          <button 
                            onClick={() => copyToClipboard(backupCode, i)}
                            className="text-slate-500 hover:text-white transition-colors"
                          >
                            {copiedCode === i ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <Button
                      variant="outline"
                      className="w-full mt-4"
                      onClick={() => downloadBackupCodes(setupData.backup_codes)}
                    >
                      <Download size={14} className="mr-2" />
                      Download Backup Codes
                    </Button>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      onClick={resetSetupDialog}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="2fa-finish-btn"
                    >
                      <Check size={14} className="mr-2" />
                      Done
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============================================
          DISABLE DIALOG
          ============================================ */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="max-w-md bg-stone-900 border-slate-700" data-testid="2fa-disable-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="text-amber-400" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will remove the extra security layer from your account
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-sm text-red-400">
                <strong>Warning:</strong> Disabling 2FA reduces your account security. Critical admin actions will no longer require additional verification.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Enter your 2FA code to confirm</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                className="bg-stone-800 border-slate-700 text-center text-lg tracking-widest font-mono"
                maxLength={8}
                data-testid="2fa-disable-code-input"
              />
              <p className="text-xs text-slate-500">Enter code from authenticator or a backup code</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Reason (optional)</Label>
              <Input
                type="text"
                placeholder="Why are you disabling 2FA?"
                value={disableReason}
                onChange={(e) => setDisableReason(e.target.value)}
                className="bg-stone-800 border-slate-700"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDisableDialog(false); setCode(''); setDisableReason(''); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDisable}
              disabled={code.length < 6 || processing}
              data-testid="2fa-disable-confirm-btn"
            >
              {processing ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <ShieldOff size={14} className="mr-2" />
              )}
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================
          REGENERATE BACKUP CODES DIALOG
          ============================================ */}
      <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <DialogContent className="max-w-md bg-stone-900 border-slate-700" data-testid="2fa-regenerate-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RefreshCw className="text-amber-400" />
              Regenerate Backup Codes
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Generate new backup codes. Old codes will be invalidated.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <p className="text-sm text-amber-400">
                All existing backup codes will stop working when you generate new ones.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Enter your 2FA code</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="bg-stone-800 border-slate-700 text-center text-lg tracking-widest font-mono"
                maxLength={6}
                data-testid="2fa-regenerate-code-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRegenerateDialog(false); setCode(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleRegenerateBackupCodes}
              disabled={code.length !== 6 || processing}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="2fa-regenerate-confirm-btn"
            >
              {processing ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <RefreshCw size={14} className="mr-2" />
              )}
              Regenerate Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================
          NEW BACKUP CODES DISPLAY DIALOG
          ============================================ */}
      <Dialog open={showBackupCodesDialog} onOpenChange={setShowBackupCodesDialog}>
        <DialogContent className="max-w-md bg-stone-900 border-slate-700" data-testid="2fa-new-codes-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="text-amber-400" />
              Your New Backup Codes
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Save these codes in a secure location
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <p className="text-sm text-amber-400">
                These codes replace your old backup codes. Each can only be used once.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {newBackupCodes.map((backupCode, i) => (
                <div key={i} className="flex items-center justify-between bg-stone-800 px-3 py-2 rounded">
                  <code className="text-sm text-slate-300 font-mono">{backupCode}</code>
                  <button 
                    onClick={() => copyToClipboard(backupCode, `new-${i}`)}
                    className="text-slate-500 hover:text-white transition-colors"
                  >
                    {copiedCode === `new-${i}` ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  </button>
                </div>
              ))}
            </div>
            
            <Button
              variant="outline"
              className="w-full"
              onClick={() => downloadBackupCodes(newBackupCodes)}
            >
              <Download size={14} className="mr-2" />
              Download Backup Codes
            </Button>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => { setShowBackupCodesDialog(false); setNewBackupCodes([]); }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TwoFactorSettingsPage;
